<template>
  <div class="personal-settings-page">
    <div class="page-header">
      <div>
        <p class="eyebrow">MY SETTINGS</p>
        <h1>個人設定</h1>
        <p>ご自身の連絡先、SlackメンバーID、パスワードを管理します。会社が管理する所属・権限情報等は変更できません。</p>
      </div>
    </div>

    <!--
      上端に出すのは読み込みの失敗だけ。保存・変更の結果は、押したボタンのある
      カードの中（ボタンのすぐ上）に出す。上端に出して画面ごと上へ戻していたので、
      一番下のパスワードのカードで押すと、押した場所を見失った。
    -->
    <CAlert v-if="errorMessage" color="danger" close-button @update:show="errorMessage = ''">
      {{ errorMessage }}
    </CAlert>

    <div v-if="loading" class="personal-settings-loading">
      <CSpinner color="primary"/>
      <span>個人設定を読み込んでいます…</span>
    </div>

    <template v-else>
      <CCard class="settings-card company-managed-card">
        <CCardHeader>
          <div class="section-heading">
            <span class="section-icon"><CIcon name="cil-building"/></span>
            <div>
              <strong>会社が管理する情報</strong>
              <p>給与、勤怠、所属、権限等に影響するため、変更が必要な場合は自社管理者へ依頼してください。</p>
            </div>
          </div>
        </CCardHeader>
        <CCardBody>
          <dl class="read-only-grid">
            <div><dt>会社名</dt><dd>{{ display(setting.company_name) }}</dd></div>
            <div><dt>社員番号</dt><dd>{{ display(setting.employee_id) }}</dd></div>
            <div><dt>氏名</dt><dd>{{ display(setting.name) }}</dd></div>
            <div><dt>ログインメール</dt><dd>{{ display(setting.email) }}</dd></div>
            <div><dt>所属部署</dt><dd>{{ display(setting.department_name) }}</dd></div>
            <div><dt>入社日</dt><dd>{{ formatDate(setting.hire_date) }}</dd></div>
            <div class="read-only-grid__roles">
              <dt>権限グループ</dt>
              <dd>
                <span class="role-list">
                  <CBadge v-for="role in roleNames" :key="role" color="info">{{ role }}</CBadge>
                  <span v-if="!roleNames.length" class="text-muted">未設定</span>
                </span>
              </dd>
            </div>
          </dl>
        </CCardBody>
      </CCard>

      <ValidationObserver ref="profileObserver">
        <form @submit.prevent="saveProfile">
          <CCard class="settings-card personal-managed-card">
            <CCardHeader>
              <div class="section-heading">
                <span class="section-icon"><CIcon name="cil-user"/></span>
                <div>
                  <strong>自分で変更できる情報</strong>
                  <p>本人の連絡先とSlack上の利用者識別情報です。入力内容は現在ログインしている自分だけに保存されます。</p>
                </div>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol sm="12" md="6">
                  <ValidationProvider name="電話番号" rules="tel" v-slot="provider">
                    <label>電話番号</label>
                    <CInput
                      v-model.trim="profileForm.telephone"
                      inputmode="numeric"
                      autocomplete="tel"
                      placeholder="例：09012345678"
                      description="ハイフンなしの半角数字で入力してください"
                    />
                    <p class="error">{{ provider.errors[0] }}</p>
                  </ValidationProvider>
                </CCol>

                <CCol sm="12" md="8">
                  <ValidationProvider name="住所" rules="address|max:60" v-slot="provider">
                    <label>住所</label>
                    <CInput
                      v-model.trim="profileForm.address"
                      autocomplete="street-address"
                      placeholder="例: 東京都千代田区…"
                    />
                    <p class="error">{{ provider.errors[0] }}</p>
                  </ValidationProvider>
                </CCol>

                <CCol sm="12" md="6">
                  <ValidationProvider name="SlackメンバーID" rules="max:20" v-slot="provider">
                    <label>SlackメンバーID</label>
                    <CInput
                      v-model.trim="profileForm.slack_id"
                      placeholder="例：U0123456789"
                      description="チャンネルIDではなく、ご自身のSlackメンバーIDを入力してください。"
                    />
                    <p class="error">{{ provider.errors[0] }}</p>
                  </ValidationProvider>
                </CCol>
              </CRow>
              <ActionResultMessage :text="profileMessage.text" :type="profileMessage.type" />
            </CCardBody>
            <CCardFooter class="card-actions">
              <CButton
                type="submit"
                color="primary"
                :disabled="savingProfile"
              >
                {{ savingProfile ? '保存しています' : '個人情報を保存' }}
              </CButton>
            </CCardFooter>
          </CCard>
        </form>
      </ValidationObserver>

      <ValidationObserver ref="passwordObserver">
        <form @submit.prevent="changePassword">
          <CCard class="settings-card password-card">
            <CCardHeader>
              <div class="section-heading">
                <span class="section-icon"><CIcon name="cil-lock-locked"/></span>
                <div>
                  <strong>パスワード変更</strong>
                  <p>現在のパスワードを入力して変更します。管理者を含め、他者がこのパスワードを閲覧することはできません。</p>
                </div>
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol sm="12" md="6">
                  <ValidationProvider name="現在のパスワード" rules="required" v-slot="provider">
                    <label>現在のパスワード<span class="asterisk">*</span></label>
                    <CInput
                      type="password"
                      v-model="passwordForm.current_password"
                      autocomplete="current-password"
                    />
                    <p class="error">{{ provider.errors[0] }}</p>
                  </ValidationProvider>
                </CCol>
              </CRow>

              <CRow>
                <CCol sm="12" md="6">
                  <ValidationProvider name="新しいパスワード" rules="required|password" v-slot="provider">
                    <label>新しいパスワード<span class="asterisk">*</span></label>
                    <CInput
                      type="password"
                      v-model="passwordForm.new_password"
                      autocomplete="new-password"
                      description="12文字以上64文字以内で入力してください。"
                    />
                    <p class="error">{{ provider.errors[0] }}</p>
                  </ValidationProvider>
                </CCol>

                <CCol sm="12" md="6">
                  <ValidationProvider
                    name="新しいパスワード（確認）"
                    rules="required|password|check:新しいパスワード"
                    v-slot="provider"
                  >
                    <label>新しいパスワード（確認）<span class="asterisk">*</span></label>
                    <CInput
                      type="password"
                      v-model="passwordForm.new_password_confirmation"
                      autocomplete="new-password"
                    />
                    <p class="error">{{ provider.errors[0] }}</p>
                  </ValidationProvider>
                </CCol>
              </CRow>
              <ActionResultMessage :text="passwordMessage.text" :type="passwordMessage.type" />
            </CCardBody>
            <CCardFooter class="card-actions">
              <CButton
                type="submit"
                color="primary"
                :disabled="savingPassword"
              >
                {{ savingPassword ? '変更中…' : 'パスワードを変更' }}
              </CButton>
            </CCardFooter>
          </CCard>
        </form>
      </ValidationObserver>
    </template>
  </div>
</template>

<script>
import api from '@/services/personalSettings'
import { scrollToFirstInvalid } from '@/mixins/submitWhenValidMixin'
import ActionResultMessage from '@/components/ActionResultMessage.vue'

const emptySetting = () => ({
  company_name: '',
  employee_id: '',
  name: '',
  email: '',
  department_name: '',
  hire_date: '',
  role_names: [],
  telephone: '',
  address: '',
  slack_id: ''
})

const emptyPassword = () => ({
  current_password: '',
  new_password: '',
  new_password_confirmation: ''
})

export default {
  name: 'PersonalSettings',
  components: { ActionResultMessage },
  data () {
    return {
      loading: false,
      savingProfile: false,
      savingPassword: false,
      // 読み込みの失敗だけ（画面の上端に出す）。
      errorMessage: '',
      // 個人情報の保存の結果。そのカードの［保存］のすぐ上に出す。
      profileMessage: { type: 'success', text: '' },
      // パスワードの変更の結果。そのカードの［変更］のすぐ上に出す。
      passwordMessage: { type: 'success', text: '' },
      setting: emptySetting(),
      profileForm: { telephone: '', address: '', slack_id: '' },
      passwordForm: emptyPassword()
    }
  },
  computed: {
    roleNames () {
      return Array.isArray(this.setting.role_names) ? this.setting.role_names : []
    }
  },
  mounted () {
    this.loadSetting()
  },
  methods: {
    /**
     * @param quiet 保存したあとの読み直しでは true。**カードを作り直さない。**
     *   loading を立てるとカードが消えて作り直され、中に出した結果の文も
     *   作り直される（そこまで画面が動かない）。
     */
    async loadSetting (quiet) {
      if (!quiet) this.loading = true
      this.errorMessage = ''
      try {
        const result = await api.get()
        this.setting = Object.assign(emptySetting(), result || {})
        this.profileForm = {
          telephone: this.setting.telephone || '',
          address: this.setting.address || '',
          slack_id: this.setting.slack_id || ''
        }
      } catch (error) {
        this.errorMessage = this.resolveError(error, '個人設定情報を取得できませんでした。')
      } finally {
        this.loading = false
      }
    },
    async saveProfile () {
      // 入力の確認を待つあいだに押し直されても2本目を走らせないよう、先に保存中にする
      if (this.savingProfile) return
      this.savingProfile = true
      this.profileMessage = { type: 'success', text: '' }
      try {
        const valid = await this.$refs.profileObserver.validate()
        if (!valid) {
          scrollToFirstInvalid(this, this.$refs.profileObserver)
          return
        }
        await api.updateProfile(this.profileForm)
        this.profileMessage = { type: 'success', text: '個人情報を更新しました。' }
        await this.loadSetting(true)
      } catch (error) {
        this.profileMessage = { type: 'danger', text: this.resolveError(error, '個人情報を更新できませんでした。') }
      } finally {
        this.savingProfile = false
      }
    },
    async changePassword () {
      // 入力の確認を待つあいだに押し直されても2本目を走らせないよう、先に変更中にする
      if (this.savingPassword) return
      this.savingPassword = true
      this.passwordMessage = { type: 'success', text: '' }
      try {
        const valid = await this.$refs.passwordObserver.validate()
        if (!valid) {
          scrollToFirstInvalid(this, this.$refs.passwordObserver)
          return
        }
        await api.updatePassword({
          current_password: this.passwordForm.current_password,
          new_password: this.passwordForm.new_password
        })
        this.passwordForm = emptyPassword()
        this.$nextTick(() => this.$refs.passwordObserver.reset())
        this.passwordMessage = { type: 'success', text: 'パスワードを変更しました。次回のログインから新しいパスワードをご使用ください。' }
      } catch (error) {
        this.passwordMessage = { type: 'danger', text: this.resolveError(error, 'パスワードを変更できませんでした。') }
      } finally {
        this.savingPassword = false
      }
    },
    display (value) {
      return value == null || String(value).trim() === '' ? '未設定' : value
    },
    formatDate (value) {
      if (!value) return '未設定'
      const parts = String(value).split('-')
      return parts.length === 3 ? `${parts[0]}/${parts[1]}/${parts[2]}` : String(value)
    },
    resolveError (error, fallback) {
      const data = error && error.response && error.response.data
      if (data && typeof data === 'object') {
        return data.message || fallback
      }
      if (typeof data === 'string' && data) return data
      // axiosが付ける英語のmessage（Network Error など）は画面へ出さない。
      // 画面側が自分で投げたErrorの日本語だけを通す。
      if (error && !error.isAxiosError && error.message) return error.message
      return fallback
    }
  }
}
</script>

<style scoped>
.personal-settings-page { min-width: 0; padding: 24px; }
.page-header { margin-bottom: 24px; }
.page-header h1 { margin: 0 0 8px; color: #17384d; font-weight: 800; }
.page-header p { max-width: 800px; margin: 0; color: #607884; line-height: 1.7; overflow-wrap: anywhere; }
.eyebrow { margin: 0 0 4px; color: #0a6ca8; font-size: 12px; font-weight: 800; letter-spacing: .14em; }
.personal-settings-loading { display: flex; min-height: 260px; align-items: center; justify-content: center; gap: 12px; color: #607884; }
.settings-card { min-width: 0; margin-bottom: 18px; }
.section-heading { display: flex; align-items: flex-start; gap: 12px; min-width: 0; }
.section-heading > div { min-width: 0; }
.section-heading strong { color: #17384d; font-size: 16px; }
.section-heading p { margin: 3px 0 0; color: #6a7f8c; font-size: 12px; line-height: 1.6; overflow-wrap: anywhere; }
.section-icon { display: inline-flex; width: 38px; height: 38px; flex: 0 0 auto; align-items: center; justify-content: center; border-radius: 12px; background: #e9f4fb; color: #0a6ca8; }
.read-only-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin: 0; }
.read-only-grid > div { min-width: 0; padding: 14px; border: 1px solid #dfe8ee; border-radius: 12px; background: #f8fbfd; }
.read-only-grid dt { margin-bottom: 6px; color: #617887; font-size: 12px; font-weight: 800; }
.read-only-grid dd { min-width: 0; margin: 0; color: #17384d; font-size: 14px; font-weight: 700; overflow-wrap: anywhere; }
.read-only-grid__roles { grid-column: span 3; }
.role-list { display: flex; flex-wrap: wrap; gap: 6px; }
.role-list .badge { white-space: normal; line-height: 1.4; }
label { color: #314f62; font-size: 12px; font-weight: 800; }
.error { min-height: 18px; margin: -8px 0 6px; color: #c93442; font-size: 12px; }
.asterisk { margin-left: 3px; color: #d33d4c; }
.card-actions { display: flex; justify-content: flex-end; }
.card-actions .btn { min-width: 190px; min-height: 44px; }

@media (max-width: 991.98px) {
  .read-only-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .read-only-grid__roles { grid-column: span 2; }
}

@media (max-width: 767.98px) {
  .personal-settings-page { padding: 12px 0; }
  .page-header { margin-bottom: 16px; }
  .page-header h1 { font-size: 28px; line-height: 1.25; }
  .read-only-grid { grid-template-columns: minmax(0, 1fr); }
  .read-only-grid__roles { grid-column: auto; }
  .card-actions { display: block; padding: 12px; }
  .card-actions .btn { width: 100%; min-width: 0; }
}
</style>
